<template>
  <div class="copilot-button-container">
    <CraftyCopilotImage
      alt="Avi, Your Snack Navigator"
      background-color="var(--salt)"
      box-shadow="large"
      class="copilot-button-container__button cr-cursor-pointer"
      data-testid="crafty-copilot-button"
      mode="portrait"
      @click="isDrawerOpen = true"
    />
    <SideDrawer
      :allow-body-scroll="allowBodyScroll"
      floating
      hide-overlay
      :is-open="isDrawerOpen"
      width="454px"
      x-position="right"
      y-position="bottom"
      @close="isDrawerOpen = false"
      @hovering="isHoveringDrawer = $event"
    >
      <template #header>
        <CraftyCopilotHeader
          :show-back-button="hasNavigationHistory"
          :show-history-button="!currentPageIsHistoryPage"
          :show-home-button="chatStarted || currentPageIsHistoryPage"
          :text="headerText"
          @click:back="goBack"
          @click:history="navigateTo({ threadId: null, title: 'Chat History' } as HistoryPage)"
          @click:home="clearHistory"
        />
      </template>
      <template #content>
        <div class="crafty-copilot cr-flex cr-flex-col cr-font-poppins">
          <div class="crafty-copilot__main-content spc-px-2 cr-flex cr-w-100 cr-justify-center">
            <CraftyCopilotChat
              v-if="!currentPageIsHistoryPage"
              :thread-id="currentPage.threadId"
              @chat-started="handleNewChatStarted"
            />
            <CraftyCopilotHistory
              v-else-if="currentPageIsHistoryPage"
              :is-history-loading="isHistoryLoading"
              :threads="threads"
              @select-thread="navigateTo($event as ThreadPage)"
            />
          </div>
        </div>
      </template>
    </SideDrawer>
  </div>
</template>

<script lang="ts">
import CraftyCopilotChat from './CraftyCopilotChat.vue'
import CraftyCopilotHistory from './CraftyCopilotHistory.vue'
import SideDrawer from '../components/SideDrawer.vue'
import CraftyCopilotImage from './CraftyCopilotImage.vue'
import { ClientHomeService } from '../services/api'
import CraftyCopilotHeader from './CraftyCopilotHeader.vue'

type DefaultPage = {
  threadId: null
  title: 'Avi, Your Snack Navigator'
}

export type ThreadPage = {
  threadId: string
  title: string
}

type HistoryPage = {
  threadId: null
  title: 'Chat History'
}

export type CopilotPage = HistoryPage | ThreadPage | DefaultPage

export type Thread = {
  id: string,
  subject: string,
  updated_at: string
}

export default {
  name: 'CraftyCopilot',
  components: {
    CraftyCopilotChat,
    CraftyCopilotHistory,
    SideDrawer,
    CraftyCopilotHeader,
    CraftyCopilotImage,
  },
  data() {
    return {
      isDrawerOpen: false,
      navigationStack: [] as CopilotPage[],
      isHistoryLoading: false,
      threads: [] as Thread[],
      isHoveringDrawer: false,
    }
  },
  computed: {
    currentPage(): CopilotPage {
      if (!this.hasNavigationHistory)
        return { threadId: null, title: 'Avi, Your Snack Navigator' } as DefaultPage
      return this.navigationStack[this.navigationStack.length - 1]
    },
    currentPageIsHistoryPage(): boolean {
      return this.isHistoryPage(this.currentPage)
    },
    headerText(): string {
      return this.currentPage.title ?? 'Avi, Your Snack Navigator'
    },
    hasNavigationHistory(): boolean {
      return this.navigationStack.length > 0
    },
    hasThreadId(): boolean {
      return !!this.currentPage.threadId
    },
    chatStarted(): boolean {
      const hasThreadId = !!this.currentPage.threadId
      return hasThreadId
    },
    isMobile(): boolean {
      return this.$screenSize.value < 768
    },
    allowBodyScroll(): boolean {
      if (this.isMobile)
        return false

      return !this.isHoveringDrawer
    },
  },
  watch: {
    isDrawerOpen(newValue: boolean) {
      if (!newValue)
        return

      this.fetchHistory()
    },
  },
  methods: {
    navigateTo(page: CopilotPage): void {
      this.navigationStack.push(page)
    },
    goBack(): void {
      if (this.navigationStack.length <= 0)
        return

      this.navigationStack.pop()
    },
    clearHistory(): void {
      this.navigationStack = []
    },
    async fetchHistory(): Promise<void> {
      this.isHistoryLoading = true
      try {
        this.threads = await ClientHomeService.fetchThreads()
      } catch (error) {
        console.error('Error fetching threads:', error)
      } finally {
        this.isHistoryLoading = false
      }
    },
    handleNewChatStarted(threadPage: ThreadPage): void {
      this.navigateTo(threadPage)
      this.fetchHistory()
    },
    isHistoryPage(page: CopilotPage): page is HistoryPage {
      return page && page.threadId === null && page.title === 'Chat History'
    },
  },
}
</script>

<style lang="scss" scoped>
:deep(.pepper-700 .base-button__icon-container) {
  color: var(--pepper-700);
}

:global(.side-drawer .drawer.side-drawer--no-title-gap .side-drawer__content) {
  &:has(.crafty-copilot) {
    margin-top: 0;
  }
}

.copilot-button-container {
  z-index: 51;
  position: fixed;
  bottom: var(--space-5);
  right: var(--space-5);
  width: 48px;
  height: 48px;

  &__button {
    &:hover {
      :deep(.crafty-image__img) {
        transform: scale(1.8);
        transform-origin: 20px top;
      }
    }
  }

  &:hover {
    width: 56px;
    height: 56px;
  }
}

:global(.drawer .side-drawer__header) {
  &:has(.crafty-copilot__drawer__header) {
    margin-top: 0;
    border-bottom: 1px solid var(--pepper-200);
  }
}

@media (min-width: 768px) {
  :global(.drawer:has(.crafty-copilot)) {
    height: 80vh;
  }
}

:global(.drawer:has(.crafty-copilot)) {
  box-shadow: 0px 0px 16px 1px #B1B6E7;
}

:global(.drawer .side-drawer__content:has(.crafty-copilot)) {
  display: flex;
}

.crafty-copilot {
  width: 100%;

  &__main-content {
    height: 100%;
    overflow: hidden;
  }
}
</style>
